// Applies a validated /give payload to localStorage — the ONLY way a give
// reaches a save. Written against StorageLike so gives flow through the
// synced-storage decorator and sync to game_saves like any other progress.
// Payloads cross a trust line (a DB row), so everything is re-validated here;
// bounds mirror dev_give's SQL checks.
import { COIN_BALANCE_CAP, type StorageLike } from './saveSync';
import { EVOLVE_TOWERS, SHOP_ORDER } from '../config/unlocks';
import { FEATURE_FLAGS, isFeatureFlag } from '../config/featureFlags';
import { LAST_LEVEL_ID } from '../config/levels';
import { HUB_LEVELS } from '../config/hubLevels';
import { potionFromCommandName, POTIONS } from '../config/potions';
import { ACHIEVEMENTS, isAchievementId, type Achievement } from '../config/achievements';
import { ACHIEVEMENTS_KEY, RankService } from '../services/rankService';
import { MAX_RANK } from '../services/rankMath';
import { bumpStat } from '../config/lifetimeStats';
import {
  AVATAR_ICONS_KEY,
  isShopAvatarCode,
  shopAvatar,
  SHOP_AVATAR_CODES,
} from '../config/avatars';
import { GIFT_ITEM_ART, isGiftItem } from '../config/giftItems';
import { BAG_PREP_MS, GOLDEN_BAG_PREP_MS, remainingMs } from '../config/restaurant';
import { CAPSULE_PREP_MS } from '../config/capybaraEvent';

const BOUNDS = {
  // An admin's bound (spec 2026-10-06); dev_give holds devs to 1 000 000.
  coins: [1, 1_000_000_000],
  bags: [1, 1_000],
  level: [2, 1_000],
  hublevel: [1, HUB_LEVELS.length],
  debris: [1, 100],
  moonstone: [1, 1],
  capycoins: [1, 1_000_000],
  capsules: [1, 1_000],
  goldenbags: [1, 1_000],
  darkcoins: [1, 1_000_000],
  lightcoins: [1, 1_000_000],
} as const;

function readInt(storage: StorageLike, key: string, fallback: number): number {
  const n = parseInt(storage.getItem(key) ?? '', 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function appendUnique(storage: StorageLike, key: string, value: string): void {
  let list: unknown = [];
  try {
    list = JSON.parse(storage.getItem(key) ?? '[]');
  } catch {
    list = [];
  }
  const arr = Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
  if (!arr.includes(value)) storage.setItem(key, JSON.stringify([...arr, value]));
}

/** Callbacks fired only when a give had a REAL effect (drives the toasts). */
export interface GiveEffects {
  /** An achievement was newly earned via /give achieve. */
  onAchieve?: (a: Achievement) => void;
  /** The rank was set via /give rank. */
  onRankSet?: (rank: number) => void;
  /** /give achieve all landed; the count is how many were NEWLY earned. */
  onAchieveAll?: (newlyEarned: number) => void;
  /** /give achieve reset landed and the list was cleared. */
  onAchieveReset?: () => void;
}

export function applyGive(storage: StorageLike, payload: unknown, effects?: GiveEffects): boolean {
  if (typeof payload !== 'object' || payload === null) return false;
  const p = payload as Record<string, unknown>;
  const what = p.what;
  const value = p.value;

  if (what === 'rank') {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > MAX_RANK) {
      return false;
    }
    new RankService(storage).setRank(value);
    effects?.onRankSet?.(value);
    return true;
  }

  if (what === 'achieve') {
    if (value === 'all') {
      // Award everything unearned (exp each, via the idempotent award()).
      // The caller shows ONE summary toast, not one per achievement.
      const svc = new RankService(storage);
      let newly = 0;
      for (const a of ACHIEVEMENTS) if (svc.award(a.id)) newly++;
      effects?.onAchieveAll?.(newly);
      return true;
    }
    if (value === 'reset') {
      // Achievements only — rank and exp are kept (spec decision).
      storage.setItem(ACHIEVEMENTS_KEY, '[]');
      effects?.onAchieveReset?.();
      return true;
    }
    if (!isAchievementId(value)) return false;
    const a = new RankService(storage).award(value);
    // Already earned → still a valid, successfully-handled delivery; the
    // recipient just gets no exp and no notification (never twice).
    if (a) effects?.onAchieve?.(a);
    return true;
  }

  if (
    what === 'coins' ||
    what === 'bags' ||
    what === 'level' ||
    what === 'hublevel' ||
    what === 'debris' ||
    what === 'moonstone' ||
    what === 'capycoins' ||
    what === 'capsules' ||
    what === 'goldenbags' ||
    what === 'darkcoins' ||
    what === 'lightcoins'
  ) {
    const [lo, hi] = BOUNDS[what];
    if (typeof value !== 'number' || !Number.isInteger(value) || value < lo || value > hi) {
      return false;
    }
    if (what === 'coins')
      // Capped: the column is a 32-bit int, and a balance past it breaks EVERY save.
      storage.setItem(
        'hambi.coins',
        String(Math.min(COIN_BALANCE_CAP, readInt(storage, 'hambi.coins', 0) + value)),
      );
    else if (what === 'bags')
      storage.setItem('hambi.bags', String(readInt(storage, 'hambi.bags', 0) + value));
    else if (what === 'debris')
      storage.setItem('hambi.debris', String(readInt(storage, 'hambi.debris', 0) + value));
    else if (what === 'capycoins')
      storage.setItem('hambi.capycoins', String(readInt(storage, 'hambi.capycoins', 0) + value));
    else if (what === 'darkcoins')
      storage.setItem('hambi.darkCoins', String(readInt(storage, 'hambi.darkCoins', 0) + value));
    else if (what === 'lightcoins')
      storage.setItem('hambi.lightCoins', String(readInt(storage, 'hambi.lightCoins', 0) + value));
    else if (what === 'capsules')
      storage.setItem('hambi.capsules', String(readInt(storage, 'hambi.capsules', 0) + value));
    else if (what === 'goldenbags')
      storage.setItem('hambi.goldenBags', String(readInt(storage, 'hambi.goldenBags', 0) + value));
    else if (what === 'moonstone')
      // One exists ever: a gift puts it on the shelf, and never takes a USED
      // one back off — the stone has already been spent.
      storage.setItem(
        'hambi.moonstone',
        storage.getItem('hambi.moonstone') === 'used' ? 'used' : 'stored',
      );
    else if (what === 'hublevel')
      // Raise-only, like level: hub levels 1..N are marked won.
      storage.setItem('hambi.hub', String(Math.max(readInt(storage, 'hambi.hub', 0), value)));
    else
      storage.setItem(
        'hambi.unlocked',
        String(Math.max(readInt(storage, 'hambi.unlocked', 1), value)),
      );
    return true;
  }

  if (what === 'tower') {
    if (typeof value !== 'string' || !(SHOP_ORDER as readonly string[]).includes(value))
      return false;
    appendUnique(storage, 'hambi.granted', value);
    return true;
  }

  if (what === 'icon') {
    // An Avatar Shop icon, by code. Appended without a coin charge — a gift is
    // not a purchase — and deduplicated, so two gifts are still one icon.
    if (!isShopAvatarCode(value)) return false;
    appendUnique(storage, AVATAR_ICONS_KEY, value);
    return true;
  }

  if (what === 'unlock') {
    if (!isFeatureFlag(value)) return false;
    appendUnique(storage, 'hambi.flags', value);
    return true;
  }

  if (what === 'item') {
    // One Storage item (spec 2026-09-22). The one-ever items are put back on
    // the shelf even if the player's own was used or opened — see giftItems.
    if (!isGiftItem(value)) return false;
    const bump = (key: string) => storage.setItem(key, String(readInt(storage, key, 0) + 1));
    if (value === 'diamond') storage.setItem('hambi.diamond', 'stored');
    else if (value === 'varietyorb') storage.setItem('hambi.orb', 'stored');
    else if (value === 'moonstone') storage.setItem('hambi.moonstone', 'stored');
    else if (value === 'debris') bump('hambi.debris');
    else if (value === 'goldstone') bump('hambi.goldstone');
    else if (value === 'capystone') bump('hambi.capystone');
    else if (value === 'darkshard') bump('hambi.darkshard');
    else if (value === 'lightshard') bump('hambi.lightshard');
    else if (value === 'evilorb') storage.setItem('hambi.evilorb', 'stored');
    else if (value === 'tokenofshroom') storage.setItem('hambi.tokenofshroom', 'stored');
    else if (value === 'lightorb') storage.setItem('hambi.lightorb', 'stored');
    else if (value === 'darkchest') bump('hambi.darkchests');
    else if (value === 'lightchest') bump('hambi.lightchests');
    else if (value === 'shroom') bump('hambi.shroom');
    else if (value === 'snowshroom') bump('hambi.snowshroom');
    else if (value === 'goldenbag') bump('hambi.goldenBags');
    else bump('hambi.capsules');
    bumpStat(storage, 'obtains'); // Quests: a gifted item is an item obtained
    return true;
  }

  if (what === 'potion') {
    // Onto the Storage shelf (a list, duplicates allowed — two gifts, two potions).
    const kind = potionFromCommandName(value);
    if (!kind) return false;
    let list: unknown = [];
    try {
      list = JSON.parse(storage.getItem('hambi.potions') ?? '[]');
    } catch {
      list = [];
    }
    const arr = Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
    storage.setItem('hambi.potions', JSON.stringify([...arr, kind]));
    bumpStat(storage, 'obtains'); // Quests: a gifted potion is an item obtained
    return true;
  }

  return false;
}

export const OP_COINS = 100_000;

/**
 * /op: give everything. All roster towers are also marked grantedSeen so the
 * "admin has come" popup is the one announcement — not 19 queued tower intros.
 */
export function applyOp(storage: StorageLike): void {
  storage.setItem('hambi.coins', String(readInt(storage, 'hambi.coins', 0) + OP_COINS));
  storage.setItem(
    'hambi.unlocked',
    String(Math.max(readInt(storage, 'hambi.unlocked', 1), LAST_LEVEL_ID)),
  );
  for (const t of SHOP_ORDER) {
    appendUnique(storage, 'hambi.granted', t);
    appendUnique(storage, 'hambi.grantedSeen', t);
  }
  for (const t of EVOLVE_TOWERS) appendUnique(storage, 'hambi.evolved', t);
  for (const f of FEATURE_FLAGS) appendUnique(storage, 'hambi.flags', f);
  // Every Avatar Shop icon: /op means everything, cosmetics included.
  for (const c of SHOP_AVATAR_CODES) appendUnique(storage, AVATAR_ICONS_KEY, c);
  // The Hub: every hub level won (the flags above already open both gates).
  storage.setItem(
    'hambi.hub',
    String(Math.max(readInt(storage, 'hambi.hub', 0), HUB_LEVELS.length)),
  );
}

/** What `/skip` can finish, by the localStorage key of its order stamp. */
export const SKIP_TIMERS = [
  { what: 'bag', key: 'hambi.bagOrderedAt', prepMs: BAG_PREP_MS },
  { what: 'goldenbag', key: 'hambi.goldenBagOrderedAt', prepMs: GOLDEN_BAG_PREP_MS },
  { what: 'capsule', key: 'hambi.capsuleOrderedAt', prepMs: CAPSULE_PREP_MS },
] as const;
export type SkipTimer = (typeof SKIP_TIMERS)[number]['what'];

/**
 * /skip's storage half (spec 2026-10-04): every bag or capsule still cooking is
 * re-stamped as ordered exactly `prepMs` ago, so it reads READY from `now` on
 * and the player opens or stores it in the usual way. Nothing is put on the
 * shelf and nothing is opened — the skip only removes the wait.
 *
 * Anything not ordered, or already ready, is left exactly as it was. Returns
 * what it readied, in SKIP_TIMERS order. The level half (winning the level
 * being played) lives in GameScene, through `skipCurrentLevel`.
 */
export function applySkip(storage: StorageLike, now: number): SkipTimer[] {
  const readied: SkipTimer[] = [];
  for (const t of SKIP_TIMERS) {
    const raw = storage.getItem(t.key);
    if (raw == null) continue;
    const orderedAt = parseInt(raw, 10);
    if (!Number.isFinite(orderedAt)) continue;
    if (remainingMs(orderedAt, now, t.prepMs) <= 0) continue;
    storage.setItem(t.key, String(Math.floor(now - t.prepMs)));
    readied.push(t.what);
  }
  return readied;
}

/** The texture for a `/give icon` delivery's popup, or null for a bad code. */
export function iconGiftArt(value: unknown): string | null {
  if (!isShopAvatarCode(value)) return null;
  return shopAvatar(value)?.texture ?? null;
}

/** Storage art for a `/give item` delivery's popup, or null for a bad name. */
export function itemGiftArt(value: unknown): string | null {
  return isGiftItem(value) ? GIFT_ITEM_ART[value] : null;
}

/** Shop art for a `/give potion` delivery's popup, or null for a bad name. */
export function potionGiftArt(value: unknown): string | null {
  const kind = potionFromCommandName(value);
  return kind ? POTIONS[kind].art : null;
}

// ── /remove (spec 2026-10-06) ───────────────────────────────────────────────

/** The largest item/potion count one `/remove` may carry (commands.ts mirrors it). */
const REMOVE_ITEM_MAX = 1_000;

/** Every list a tower can be held in outside the episode schedule, plus its traces. */
const TOWER_HOLD_KEYS = ['hambi.owned', 'hambi.granted', 'hambi.challenge'] as const;
const TOWER_TRACE_KEYS = ['hambi.grantedSeen', 'hambi.equipped', 'hambi.evolved'] as const;

/** The count-shaped Storage keys, by `/remove` amount kind or item name. */
const REMOVE_COUNT_KEYS: Record<string, string> = {
  bags: 'hambi.bags',
  goldenbags: 'hambi.goldenBags',
  capsules: 'hambi.capsules',
  // item names
  debris: 'hambi.debris',
  goldstone: 'hambi.goldstone',
  capystone: 'hambi.capystone',
  darkshard: 'hambi.darkshard',
  lightshard: 'hambi.lightshard',
  capsule: 'hambi.capsules',
  goldenbag: 'hambi.goldenBags',
  darkchest: 'hambi.darkchests',
  lightchest: 'hambi.lightchests',
  shroom: 'hambi.shroom',
  snowshroom: 'hambi.snowshroom',
};

/**
 * The one-of items and the state a removed one is left in. Not '' — that reads
 * as "never claimed", and the next win of HTD Exhibition / Level 25 / the Moon
 * finale would simply hand it back. A removed one is as gone as a spent one.
 */
const REMOVE_ONE_OF: Record<string, { key: string; gone: string }> = {
  diamond: { key: 'hambi.diamond', gone: 'used' },
  varietyorb: { key: 'hambi.orb', gone: 'opened' },
  moonstone: { key: 'hambi.moonstone', gone: 'used' },
  // The Evil Orb is paid on its quest's FIRST win (stars-gated), so a removed
  // one can simply be '' — a replay cannot hand it back.
  evilorb: { key: 'hambi.evilorb', gone: '' },
  // The Token of Shroom: first win only, like the Evil Orb, so '' is safe.
  tokenofshroom: { key: 'hambi.tokenofshroom', gone: '' },
  // The Light Orb is paid on ANY Heaven's Peak win while it is '', so a
  // removed one is 'taken' — otherwise the next win would hand it back.
  lightorb: { key: 'hambi.lightorb', gone: 'taken' },
};

function readList(storage: StorageLike, key: string): string[] {
  let list: unknown = [];
  try {
    list = JSON.parse(storage.getItem(key) ?? '[]');
  } catch {
    list = [];
  }
  return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
}

/** Subtract from a count key, floored at 0. True when there was anything to take. */
function takeCount(storage: StorageLike, key: string, n: number): boolean {
  const have = readInt(storage, key, 0);
  if (have <= 0) return false;
  storage.setItem(key, String(Math.max(0, have - n)));
  return true;
}

/** Put a stored one-of item out of reach. True when one was on the shelf. */
function takeOneOf(storage: StorageLike, name: string): boolean {
  const o = REMOVE_ONE_OF[name];
  if (storage.getItem(o.key) !== 'stored') return false;
  storage.setItem(o.key, o.gone);
  return true;
}

/** Take up to `n` potions of one kind off the shelf. True when at least one went. */
function takePotions(storage: StorageLike, kind: string, n: number): boolean {
  const kept: string[] = [];
  let taken = 0;
  for (const p of readList(storage, 'hambi.potions')) {
    if (p === kind && taken < n) taken++;
    else kept.push(p);
  }
  if (taken === 0) return false;
  storage.setItem('hambi.potions', JSON.stringify(kept));
  return true;
}

/**
 * `/remove clear`: empty the Storage shelf — every potion, bag, capsule and
 * item. Nothing else: coins, towers, progress, a bag still COOKING (it is not
 * on the shelf yet) and any effect already running are all left alone.
 */
function clearStorage(storage: StorageLike): boolean {
  let any = false;
  for (const key of new Set(Object.values(REMOVE_COUNT_KEYS))) {
    if (takeCount(storage, key, Number.MAX_SAFE_INTEGER)) any = true;
  }
  for (const name of Object.keys(REMOVE_ONE_OF)) if (takeOneOf(storage, name)) any = true;
  if (readList(storage, 'hambi.potions').length > 0) {
    storage.setItem('hambi.potions', '[]');
    any = true;
  }
  return any;
}

/**
 * Applies a `/remove` payload (spec 2026-10-06) — /give in reverse. 'invalid'
 * for a malformed payload (it crossed a trust line), 'nothing' when the player
 * had none of it (the command then does nothing at all: no write, no popup),
 * 'removed' otherwise. Amounts floor at 0 rather than refusing.
 *
 * A tower is taken from every list that holds one outside the episode
 * schedule — bought, granted, challenge — along with its seen-intro mark,
 * loadout slot and Evolution. A tower the campaign hands out by episode is in
 * none of those, so it reads as 'nothing': removing it would mean un-playing
 * levels.
 */
export function applyRemove(
  storage: StorageLike,
  payload: unknown,
): 'removed' | 'nothing' | 'invalid' {
  if (typeof payload !== 'object' || payload === null) return 'invalid';
  const p = payload as Record<string, unknown>;
  const { what, value, amount } = p;
  const done = (changed: boolean) => (changed ? 'removed' : 'nothing');
  const n = (max: number): number | null =>
    typeof amount === 'number' && Number.isInteger(amount) && amount >= 1 && amount <= max
      ? amount
      : null;

  if (what === 'clear') return done(clearStorage(storage));

  if (what === 'coins' || what === 'darkcoins' || what === 'lightcoins') {
    const k = n(COIN_BALANCE_CAP);
    if (k == null) return 'invalid';
    const key =
      what === 'coins'
        ? 'hambi.coins'
        : what === 'darkcoins'
          ? 'hambi.darkCoins'
          : 'hambi.lightCoins';
    return done(takeCount(storage, key, k));
  }
  if (what === 'bags' || what === 'goldenbags' || what === 'capsules') {
    const k = n(COIN_BALANCE_CAP);
    if (k == null) return 'invalid';
    return done(takeCount(storage, REMOVE_COUNT_KEYS[what], k));
  }
  if (what === 'tower') {
    if (typeof value !== 'string' || !(SHOP_ORDER as readonly string[]).includes(value)) {
      return 'invalid';
    }
    if (!TOWER_HOLD_KEYS.some((key) => readList(storage, key).includes(value))) return 'nothing';
    for (const key of [...TOWER_HOLD_KEYS, ...TOWER_TRACE_KEYS]) {
      const list = readList(storage, key);
      if (list.includes(value)) {
        storage.setItem(key, JSON.stringify(list.filter((t) => t !== value)));
      }
    }
    return 'removed';
  }
  if (what === 'item') {
    const k = n(REMOVE_ITEM_MAX);
    if (!isGiftItem(value) || k == null) return 'invalid';
    if (value in REMOVE_ONE_OF) return done(takeOneOf(storage, value));
    return done(takeCount(storage, REMOVE_COUNT_KEYS[value], k));
  }
  if (what === 'potion') {
    const kind = potionFromCommandName(value);
    const k = n(REMOVE_ITEM_MAX);
    if (!kind || k == null) return 'invalid';
    return done(takePotions(storage, kind, k));
  }
  return 'invalid';
}
